/*
 * Calls WebAPI directly via api.webAPIFetch (bypassing WebLayer/Services.json)
 * and resolves to { data, error } rather than rejecting, so a feature that
 * uses it can't crash Chronos.
 *
 * WebAPI is a different address from WebLayer, so it can be unreachable while
 * the rest of Chronos works, most likely when someone is off the IPRS network.
 * Every feature that calls it should report failures the same way: an error's
 * message is fit to show a user, error.detail says what actually failed (for
 * whoever they report it to), and error.kind is one of 'unreachable',
 * 'notFound', 'notAllowed', 'server', 'notConfigured' or 'rejected' (the
 * server refused the request and error.message is its explanation, e.g. a
 * validation message). Show it with WebAPIProblem or WebAPIWarning.
 */

/* An address that doesn't answer can take a minute or more to fail */
export const WEBAPI_TIMEOUT_MS = 10000;

const webAPIError = (kind, message, detail) => Object.assign(new Error(message), { kind, detail });

const messages = feature => ({
    unreachable:    `Chronos can't reach the service for ${feature}. This usually means `
                    + 'you aren\'t connected to the IPRS network.',
    notFound:       `The service for ${feature} isn't available on this site.`,
    notAllowed:     `You don't have permission to use ${feature}, or your login has expired.`,
    server:         `The service for ${feature} had a problem.`,
    notConfigured:  `The service for ${feature} isn't set up on this Chronos site.`,
});

const httpError = (status, statusText, where, text) => {
    const detail = `${where}: HTTP ${status} ${statusText ?? ''}`.trim();
    if (401 === status || 403 === status) {
        return webAPIError('notAllowed', text.notAllowed, detail);
    }
    if (404 === status) {
        return webAPIError('notFound', text.notFound, detail);
    }
    return webAPIError('server', text.server, detail);
};

/**
 * @param {object} api       iprs-api-library API, with webAPIBaseURL set
 * @param {string} route     e.g. '/WorkingHours/Party/5'
 * @param {object} [body]    POSTed as JSON when given, otherwise a GET
 * @param {string} [feature] what the call is for, in user terms, e.g. 'working hours'
 * @returns {Promise<{data: *, error: Error|null}>} data is the envelope's Result
 */
const webAPICall = async (api, route, body = null, feature = 'this feature') => {
    const text = messages(feature);
    const method = body ? 'POST' : 'GET';
    const where = `${method} ${route}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), WEBAPI_TIMEOUT_MS);
    try {
        const options = body
            ? { method, body: JSON.stringify(body), signal: controller.signal }
            : { method, signal: controller.signal };
        const response = await api.webAPIFetch(route, options);
        if (!response.ok) {
            return { data: null, error: httpError(response.status, response.statusText, where, text) };
        }
        const json = await response.json();
        if (json.Status !== 'OK') {
            return { data: null, error: webAPIError('rejected', json.Text || text.server,
                `${where}: ${json.Code ?? ''} ${json.Text ?? ''}`.trim()) };
        }
        return { data: json.Result, error: null };
    } catch (error) {
        if ('AbortError' === error?.name) {
            return { data: null, error: webAPIError('unreachable', text.unreachable,
                `${where}: no reply after ${WEBAPI_TIMEOUT_MS / 1000} seconds`) };
        }
        if (/webAPIBaseURL/.test(error?.message ?? '')) {
            return { data: null, error: webAPIError('notConfigured', text.notConfigured,
                `${where}: ${error.message}`) };
        }
        if (error instanceof SyntaxError) {
            return { data: null, error: webAPIError('server', text.server,
                `${where}: the reply wasn't JSON (${error.message})`) };
        }
        // fetch rejects with a TypeError for no network, DNS, CORS or certificate failures
        return { data: null, error: webAPIError('unreachable', text.unreachable,
            `${where}: ${error?.name ?? 'Error'}: ${error?.message}`) };
    } finally {
        clearTimeout(timer);
    }
};

export default webAPICall;
